import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Check, X, Play, FlaskConical, Clock, BatteryCharging, ChevronDown } from "lucide-react";
import SessionPlanDisplay from "../plan/SessionPlanDisplay";
import { getSlotRecommendation } from "../../config/periodisation";
import usePlanEditor from "../../hooks/usePlanEditor";
import { coerceLegacyNodeTypes } from "../../utils/planNormaliser";

// ONE panel for a programme session — view it, edit its exercises (saved to the
// slot), or start the workout. Opened by tapping the card OR its Edit action, so
// there is a single editing route (previously the inline dialog and the Session
// view were two competing editors). Tailored to the programme: a guidance strip
// shows what the framework recommends for this slot. The strip is one line that
// expands on tap, and it scrolls with the plan: pinned and fully open it took
// over half a phone screen, leaving a sliver for editing.
const EditProgrammeSessionDialog = ({
  open,
  onOpenChange,
  programme,
  session,
  onSave,
  onStart,
}) => {
  const { llmSettings, userProfile } = useContext(WorkoutContext);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recOpen, setRecOpen] = useState(false);

  const readOnly =
    !!session && (session.status === "completed" || session.status === "missed");
  const rec = useMemo(
    () => getSlotRecommendation(programme, session),
    [programme, session],
  );

  useEffect(() => {
    if (open && session?.plan_data) {
      // Normalise on read, exactly as the Session tab does (WorkoutContext at
      // mount, useWorkoutCoach for API-loaded plans). Stored programme plans
      // skipped this entirely, so one whose sets were written out as N sibling
      // Work nodes never folded into the compact sets-Group + `setScheme` form:
      // the plan showed N rows instead of one ramp, and — with no enclosing
      // sets-Group — the per-set editor never appeared (issues #42 / #69). Also
      // coerces legacy SetRest and back-fills ids that dnd-kit needs for stable
      // sortable keys. Clone first: the normaliser returns its input unchanged
      // when there is nothing to do, and the draft must never alias the session.
      setDraft(
        coerceLegacyNodeTypes(JSON.parse(JSON.stringify(session.plan_data))),
      );
    } else if (!open) {
      setDraft(null);
      setBusy(false);
      setRecOpen(false);
    }
  }, [open, session]);

  const { planDisplayProps } = usePlanEditor(setDraft);

  const payload = useCallback(
    () => ({
      label: draft?.label || session?.label || session?.scheduled_label,
      plan_data: draft,
    }),
    [draft, session],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    setBusy(true);
    try {
      await onSave(payload());
      onOpenChange(false);
    } catch {
      setBusy(false);
    }
  }, [draft, onSave, payload, onOpenChange]);

  const handleStart = useCallback(async () => {
    if (!draft) return;
    setBusy(true);
    try {
      // onStart persists the edits, then loads the plan into the coach.
      await onStart(payload());
    } catch {
      setBusy(false);
    }
  }, [draft, onStart, payload]);

  const stats = rec
    ? [
        ["Sets", rec.sets],
        ["Reps", rec.reps],
        ["Rest", rec.rest],
        ["Target", rec.intensity],
      ].filter(([, v]) => v)
    : [];
  const recSummary = rec
    ? [
        rec.sets && rec.reps ? `${rec.sets} × ${rec.reps}` : null,
        rec.intensity,
        rec.durationMin ? `~${rec.durationMin} min` : null,
      ]
        .filter(Boolean)
        .join(" · ") || rec.dayType
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90dvh] flex flex-col overflow-hidden p-4 sm:p-6">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {session?.label || session?.scheduled_label || "Session"}
          </DialogTitle>
          {readOnly ? (
            <DialogDescription>
              This session is complete {"—"} here's what you did.
            </DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              Edit the exercises, or start the workout.
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Completion summary for a finished session */}
        {readOnly && (session?.session_rating != null || session?.session_notes) && (
          <div className="shrink-0 rounded-lg border border-secondary/30 bg-secondary/5 p-3 text-xs space-y-1">
            {session.session_rating != null && (
              <div className="text-secondary font-medium">
                Effort {session.session_rating}/10
              </div>
            )}
            {session.session_notes && (
              <p className="text-muted-foreground italic">
                {"“"}{session.session_notes}{"”"}
              </p>
            )}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 py-2">
          {/* Programme guidance — what the framework recommends for this slot */}
          {rec && (
            <div className="mb-3 rounded-lg border border-cardio/30 bg-cardio/5 text-xs">
              <button
                type="button"
                onClick={() => setRecOpen((o) => !o)}
                aria-expanded={recOpen}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
              >
                <FlaskConical className="h-3.5 w-3.5 shrink-0 text-cardio" />
                <span className="flex min-w-0 flex-wrap gap-x-1.5">
                  <span className="font-semibold text-cardio">{rec.framework}</span>
                  {/* The grid below repeats these once open */}
                  {recSummary && !recOpen && (
                    <span className="text-muted-foreground">{recSummary}</span>
                  )}
                </span>
                <ChevronDown
                  className={`ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform ${recOpen ? "rotate-180" : ""}`}
                />
              </button>

              {rec.isDeload && (
                <div className="flex items-center gap-1.5 px-3 pb-2 text-[11px] text-yellow-500">
                  <BatteryCharging className="h-3.5 w-3.5 shrink-0" />
                  Deload week {"—"} cut volume ~50% and keep loads light.
                </div>
              )}

              {recOpen && (
                <div className="space-y-2 px-3 pb-3">
                  {(rec.dayType || rec.durationMin) && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      {rec.dayType && <span>{rec.dayType} day</span>}
                      {rec.durationMin && (
                        <span className="ml-auto inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" /> ~{rec.durationMin} min
                        </span>
                      )}
                    </div>
                  )}
                  {rec.note ? (
                    <p className="text-[11px] text-muted-foreground leading-snug">{rec.note}</p>
                  ) : (
                    stats.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {stats.map(([k, v]) => (
                          <div
                            key={k}
                            className="rounded-md bg-background/50 border border-border/40 px-2 py-1.5"
                          >
                            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
                              {k}
                            </div>
                            <div className="text-xs font-medium text-foreground truncate">
                              {v}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                  {!readOnly && (
                    <p className="text-[10px] text-muted-foreground/70 leading-snug">
                      Recommended by your programme {"—"} edits are yours to make.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {draft && (
            <SessionPlanDisplay
              plan={draft}
              editable={!readOnly}
              {...planDisplayProps}
              llmSettings={llmSettings}
              userProfile={userProfile}
            />
          )}
        </div>

        <div className="shrink-0 flex items-center justify-end gap-2 pt-3 mt-1 border-t border-border/40">
          {readOnly ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              {/* Phones close with the ✕; three buttons don't fit across */}
              <Button
                variant="ghost"
                className="hidden sm:inline-flex"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                <X className="h-4 w-4 mr-1.5" />
                Cancel
              </Button>
              <Button
                variant="outline"
                className="flex-1 sm:flex-none"
                onClick={handleSave}
                disabled={busy || !draft}
              >
                <Check className="h-4 w-4 mr-1.5" />
                Save
              </Button>
              <Button
                className="flex-1 sm:flex-none"
                onClick={handleStart}
                disabled={busy || !draft}
              >
                <Play className="h-4 w-4 mr-1.5" />
                {busy ? "Starting…" : "Start workout"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditProgrammeSessionDialog;
